import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth, homeFor } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { Toaster } from "@/components/ui/sonner";
import AppLayout from "@/components/AppLayout";
import PortalLayout from "@/components/PortalLayout";
import EnvBanner from "@/components/EnvBanner";
import LoginPage from "@/pages/LoginPage";
import PortalLoginPage from "@/pages/PortalLoginPage";
import PortalFinderPage from "@/pages/PortalFinderPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import CompaniesPage from "@/pages/CompaniesPage";
import CompanyDetailPage from "@/pages/CompanyDetailPage";
import UsersPage from "@/pages/UsersPage";
import ClientsPage from "@/pages/ClientsPage";
import ClientDetailPage from "@/pages/ClientDetailPage";
import CategoriesPage from "@/pages/CategoriesPage";
import TicketsPage from "@/pages/TicketsPage";
import TicketDetailPage from "@/pages/TicketDetailPage";
import EmailLogsPage from "@/pages/EmailLogsPage";
import AuditLogsPage from "@/pages/AuditLogsPage";
import ActivateAccountPage from "@/pages/ActivateAccountPage";
import LandingPage from "@/pages/LandingPage";
import DemoRequestsPage from "@/pages/DemoRequestsPage";
import MyAbsencePage from "@/pages/MyAbsencePage";
import { ClientHomePage, NewTicketPage } from "@/pages/ClientPortalPages";

const STAFF = ["super_admin", "company_admin", "manager", "collaborator"];

function Loading() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background" data-testid="app-loading">
      <div className="font-heading text-xl font-semibold">Suivio<span className="text-primary">360</span></div>
    </div>
  );
}

function RequireAuth({ roles, children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to={homeFor(user)} replace />;
  return children;
}

function RoleGuard({ roles, children }) {
  const { user } = useAuth();
  if (!roles.includes(user?.role)) return <Navigate to="/app" replace />;
  return children;
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  return <Navigate to={user ? homeFor(user) : "/login"} replace />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/app" element={<RequireAuth roles={STAFF}><AppLayout /></RequireAuth>}>
              <Route index element={<DashboardPage />} />
              <Route path="companies" element={<RoleGuard roles={["super_admin"]}><CompaniesPage /></RoleGuard>} />
              <Route path="companies/:id" element={<RoleGuard roles={["super_admin"]}><CompanyDetailPage /></RoleGuard>} />
              <Route path="users" element={<RoleGuard roles={["super_admin", "company_admin", "manager"]}><UsersPage /></RoleGuard>} />
              <Route path="mon-absence" element={<MyAbsencePage />} />
              <Route path="clients" element={<RoleGuard roles={["super_admin", "company_admin", "manager"]}><ClientsPage /></RoleGuard>} />
              <Route path="clients/:id" element={<RoleGuard roles={["super_admin", "company_admin", "manager"]}><ClientDetailPage /></RoleGuard>} />
              <Route path="categories" element={<RoleGuard roles={["super_admin", "company_admin", "manager"]}><CategoriesPage /></RoleGuard>} />
              <Route path="tickets" element={<TicketsPage />} />
              <Route path="tickets/:id" element={<TicketDetailPage />} />
              <Route path="emails" element={<EmailLogsPage />} />
              <Route path="demo-requests" element={<RoleGuard roles={["super_admin"]}><DemoRequestsPage /></RoleGuard>} />
              <Route path="logs" element={<RoleGuard roles={["super_admin", "company_admin"]}><AuditLogsPage /></RoleGuard>} />
            </Route>
            <Route path="/portal" element={<RequireAuth roles={["client_user"]}><PortalLayout /></RequireAuth>}>
              <Route index element={<ClientHomePage />} />
              <Route path="tickets/:id" element={<TicketDetailPage />} />
              <Route path="nouvelle-demande" element={<NewTicketPage />} />
            </Route>
            <Route path="/activer-compte/:token" element={<ActivateAccountPage />} />
            <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
            <Route path="/reinitialiser-mot-de-passe/:token" element={<ResetPasswordPage />} />
            <Route path="/portail-client" element={<PortalFinderPage />} />
            <Route path="/:slug" element={<PortalLoginPage />} />
            <Route path="*" element={<RootRedirect />} />
          </Routes>
          <EnvBanner />
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
